export const INBOX_TYPES = {
  WEB: 'Channel::WebWidget',
  FB: 'Channel::FacebookPage',
  TWITTER: 'Channel::TwitterProfile',
  TWILIO: 'Channel::TwilioSms',
  WHATSAPP: 'Channel::Whatsapp',
  API: 'Channel::Api',
  EMAIL: 'Channel::Email',
  TELEGRAM: 'Channel::Telegram',
  LINE: 'Channel::Line',
  SMS: 'Channel::Sms',
  INSTAGRAM: 'Channel::Instagram',
  TIKTOK: 'Channel::Tiktok',
  VOICE: 'Channel::Voice',
  ZERNIO_FACEBOOK: 'Channel::FacebookZernio',
  ZERNIO_INSTAGRAM: 'Channel::InstagramZernio',
  ZERNIO_TIKTOK: 'Channel::TiktokZernio',
  ZERNIO_FACEBOOK_COMMENT: 'Channel::FacebookCommentZernio',
  ZERNIO_INSTAGRAM_COMMENT: 'Channel::InstagramCommentZernio',
};

// Channels delivered through Zernio. They behave like their native
// counterparts for message status, so status indicators resolve instead of
// sitting on the pending clock forever.
export const ZERNIO_INBOX_TYPES = [
  INBOX_TYPES.ZERNIO_FACEBOOK,
  INBOX_TYPES.ZERNIO_INSTAGRAM,
  INBOX_TYPES.ZERNIO_TIKTOK,
  INBOX_TYPES.ZERNIO_FACEBOOK_COMMENT,
  INBOX_TYPES.ZERNIO_INSTAGRAM_COMMENT,
];

export const TWILIO_CHANNEL_MEDIUM = {
  WHATSAPP: 'whatsapp',
  SMS: 'sms',
};

// WhatsApp inboxes in this deployment are bridged through Evolution as
// Channel::Api inboxes named "WS-<number>-<company>" (the native Channel::Whatsapp
// inbox is unused). channel_type alone is ambiguous — Shopee/TikTok/Lazada are also
// Channel::Api — so match the WS- name convention. Reads camel or snake inbox shapes.
export const isAWhatsAppChannel = inbox => {
  const channelType = inbox?.channelType || inbox?.channel_type;
  const name = inbox?.name || '';
  return (
    channelType === INBOX_TYPES.WHATSAPP ||
    (channelType === INBOX_TYPES.API && /^WS[\s-]/i.test(name.trim()))
  );
};

// Marketplace inboxes are bridged in as Channel::Api too, named "<Platform> - <company>"
// (Shopee - MK, TikTok - S3, …). They are REPLY-ONLY: Shopee/TikTok/Lazada have no
// seller-initiated thread, so a conversation opened from our side has no provider thread
// to attach to and can never be delivered — the outbox retries, then dies with
// `shopee_no_mapping`. Same name-matching rationale as isAWhatsAppChannel above.
const MARKETPLACE_NAME_PATTERN = /^(shopee|tiktok|lazada)\b/i;

export const isAMarketplaceChannel = inbox => {
  const channelType = inbox?.channelType || inbox?.channel_type;
  const name = inbox?.name || '';
  return (
    channelType === INBOX_TYPES.API &&
    MARKETPLACE_NAME_PATTERN.test(name.trim())
  );
};

const INBOX_ICON_MAP_FILL = {
  [INBOX_TYPES.WEB]: 'i-ri-global-fill',
  [INBOX_TYPES.FB]: 'i-ri-messenger-fill',
  [INBOX_TYPES.TWITTER]: 'i-ri-twitter-x-fill',
  [INBOX_TYPES.WHATSAPP]: 'i-ri-whatsapp-fill',
  [INBOX_TYPES.API]: 'i-ri-cloudy-fill',
  [INBOX_TYPES.EMAIL]: 'i-ri-mail-fill',
  [INBOX_TYPES.TELEGRAM]: 'i-ri-telegram-fill',
  [INBOX_TYPES.LINE]: 'i-ri-line-fill',
  [INBOX_TYPES.INSTAGRAM]: 'i-ri-instagram-fill',
  [INBOX_TYPES.TIKTOK]: 'i-ri-tiktok-fill',
  [INBOX_TYPES.VOICE]: 'i-ri-phone-fill',
};

const DEFAULT_ICON_FILL = 'i-ri-chat-1-fill';

const INBOX_ICON_MAP_LINE = {
  [INBOX_TYPES.WEB]: 'i-woot-website',
  [INBOX_TYPES.FB]: 'i-woot-messenger',
  [INBOX_TYPES.TWITTER]: 'i-woot-x',
  [INBOX_TYPES.WHATSAPP]: 'i-woot-whatsapp',
  [INBOX_TYPES.API]: 'i-woot-api',
  [INBOX_TYPES.EMAIL]: 'i-woot-mail',
  [INBOX_TYPES.TELEGRAM]: 'i-woot-telegram',
  [INBOX_TYPES.LINE]: 'i-woot-line',
  [INBOX_TYPES.INSTAGRAM]: 'i-woot-instagram',
  [INBOX_TYPES.VOICE]: 'i-woot-voice',
  [INBOX_TYPES.TIKTOK]: 'i-woot-tiktok',
};

const DEFAULT_ICON_LINE = 'i-ri-chat-1-line';

// Shopee and Lazada have no brand glyph in the bundled icon sets, so they borrow
// neutral commerce icons. TikTok reuses the icons already mapped for its channel type.
const MARKETPLACE_ICON_MAP = [
  {
    pattern: /^shopee\b/i,
    fill: 'i-ri-shopping-bag-3-fill',
    line: 'i-ri-shopping-bag-3-line',
  },
  {
    pattern: /^tiktok\b/i,
    fill: INBOX_ICON_MAP_FILL[INBOX_TYPES.TIKTOK],
    line: INBOX_ICON_MAP_LINE[INBOX_TYPES.TIKTOK],
  },
  {
    pattern: /^lazada\b/i,
    fill: 'i-ri-shopping-cart-2-fill',
    line: 'i-ri-shopping-cart-2-line',
  },
];

export const getInboxSource = (type, phoneNumber, inbox) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return inbox.website_url || '';

    case INBOX_TYPES.TWILIO:
    case INBOX_TYPES.WHATSAPP:
    case INBOX_TYPES.VOICE:
      return phoneNumber || '';

    case INBOX_TYPES.EMAIL:
      return inbox.email || '';

    default:
      return '';
  }
};
export const getReadableInboxByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'livechat';

    case INBOX_TYPES.FB:
      return 'facebook';

    case INBOX_TYPES.TWITTER:
      return 'twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp') ? 'whatsapp' : 'sms';

    case INBOX_TYPES.WHATSAPP:
      return 'whatsapp';

    case INBOX_TYPES.API:
      return 'api';

    case INBOX_TYPES.EMAIL:
      return 'email';

    case INBOX_TYPES.TELEGRAM:
      return 'telegram';

    case INBOX_TYPES.LINE:
      return 'line';

    case INBOX_TYPES.VOICE:
      return 'voice';

    default:
      return 'chat';
  }
};

export const getInboxClassByType = (type, phoneNumber) => {
  switch (type) {
    case INBOX_TYPES.WEB:
      return 'globe-desktop';

    case INBOX_TYPES.FB:
      return 'brand-facebook';

    case INBOX_TYPES.TWITTER:
      return 'brand-twitter';

    case INBOX_TYPES.TWILIO:
      return phoneNumber?.startsWith('whatsapp')
        ? 'brand-whatsapp'
        : 'brand-sms';

    case INBOX_TYPES.WHATSAPP:
      return 'brand-whatsapp';

    case INBOX_TYPES.API:
      return 'cloud';

    case INBOX_TYPES.EMAIL:
      return 'mail';

    case INBOX_TYPES.TELEGRAM:
      return 'brand-telegram';

    case INBOX_TYPES.LINE:
      return 'brand-line';

    case INBOX_TYPES.INSTAGRAM:
      return 'brand-instagram';

    case INBOX_TYPES.TIKTOK:
      return 'brand-tiktok';

    case INBOX_TYPES.VOICE:
      return 'phone';

    default:
      return 'chat';
  }
};

export const getInboxIconByType = (type, medium, variant = 'fill') => {
  const iconMap =
    variant === 'fill' ? INBOX_ICON_MAP_FILL : INBOX_ICON_MAP_LINE;
  const defaultIcon =
    variant === 'fill' ? DEFAULT_ICON_FILL : DEFAULT_ICON_LINE;

  // Special case for Twilio (whatsapp and sms)
  if (type === INBOX_TYPES.TWILIO && medium === 'whatsapp') {
    return iconMap[INBOX_TYPES.WHATSAPP];
  }

  return iconMap[type] ?? defaultIcon;
};

// Every bridged inbox is Channel::Api, so getInboxIconByType renders the same generic
// cloud glyph for WhatsApp, Shopee, TikTok and Lazada alike — useless in a picker where
// the whole point is knowing which channel you are about to send on. Resolve from the
// inbox name first, then fall back to the channel-type map.
export const getInboxIconForInbox = (inbox, variant = 'fill') => {
  const channelType = inbox?.channelType || inbox?.channel_type;

  if (channelType === INBOX_TYPES.API) {
    const iconMap =
      variant === 'fill' ? INBOX_ICON_MAP_FILL : INBOX_ICON_MAP_LINE;

    if (isAWhatsAppChannel(inbox)) return iconMap[INBOX_TYPES.WHATSAPP];

    const marketplace = MARKETPLACE_ICON_MAP.find(({ pattern }) =>
      pattern.test((inbox?.name || '').trim())
    );
    if (marketplace) return marketplace[variant] ?? marketplace.fill;
  }

  return getInboxIconByType(channelType, inbox?.medium, variant);
};

export const getInboxWarningIconClass = (type, reauthorizationRequired) => {
  const allowedInboxTypes = [INBOX_TYPES.FB, INBOX_TYPES.EMAIL];
  if (allowedInboxTypes.includes(type) && reauthorizationRequired) {
    return 'warning';
  }
  return '';
};
